import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { useApp } from "../../context/AppContext";

/**
 * CrmDetailDrawer —— CRM 详情抽屉壳（客户线索详情 / 已签约客户详情共用）
 *
 * 需求来源（2026-10-13）：用户要求把「客户线索 CRM 详情」「已签约客户详情」从整页跳转
 * 改为在列表页上叠加抽屉展示，不再单独跳转页面。
 *
 * 设计对齐：做法与设计项目抽屉（pages/design/v3/ProjectDrawer.jsx）保持一致 ——
 *   ① 抽屉由 URL 驱动（/crm/leads/:id、/crm/signed/:id），直达即「列表 + 抽屉」，可刷新、可分享；
 *   ② 宽度用「比例」而非绝对值：默认 = 视口宽 × 70%，支持左边缘拖拽调宽、双击复位、localStorage 记忆；
 *   ③ 打开期间锁住文档滚动，避免页面滚动条与面板滚动条在右侧并排出现两条。
 *   ④ 手机改用满宽详情：不保留桌面的窄边遮罩/调宽手柄；顶部关闭与底部内容避开系统安全区。
 *      手机视口变化不写入桌面宽度记忆，切回电脑仍按用户原设置显示。
 *
 * 关键取舍①（层级 z-[45]，而不是设计抽屉的 z-[70]）：
 *   这两个详情页内部有大量 Radix 弹窗（components/ui/dialog.jsx 默认 z-50）。若抽屉层级高于 50，
 *   弹窗会被压在抽屉下面（表现为「点了没反应」）。取 45 可同时满足：
 *     · 高于顶栏 z-30 / 侧栏 z-40 → 抽屉能盖住它们；
 *     · 低于所有弹窗 z-50 → 抽屉里再开弹窗，弹窗正常浮在最上层。
 *   抽屉内部自己的元素（拖拽条 z-40、内容）都在 z-45 这个层叠上下文里，不会越级到弹窗之上。
 *
 * 关键取舍②（宽度上限也用比例）：
 *   上限 = 视口宽 × 92%（留 8% 缝隙便于点遮罩关闭）。若沿用绝对上限，大屏上 70vw 会被截断、拿不到 70vw；
 *   且宽度始终 ≤ 视口宽，面板不会被 w-full 压缩，拖拽条定位可直接用 panelW 计算（无测量滞后）。
 *
 * 关键取舍③（内容区 padding 与 .app-page-content 保持一致）：
 *   详情页里大量使用「负边距抵消页面留白」的写法来做到「通栏」：上留白用
 *   `-mt-4 sm:-mt-5 lg:-mt-6` 抵消，左右用 `.page-bleed-x` 抵消、再用 `.page-pad-x` 回补。
 *   这里的内容区左右 padding 同样取 `.page-pad-x`、上下取与 `.app-page-content` 同档的
 *   `pt/pb-4 sm:pt/pb-5 lg:pt/pb-6`，这些抵消写法就能原样生效、通栏吸顶条依旧贴边，
 *   不必逐个页面改数值。2026-10-13 页面留白由 16/24/28px 收为 16/20/24px，此处同步跟随。
 *
 * @param {string} title    顶部细栏标题（抽屉自己的标识，内容区各页仍保留自身表头）
 * @param {Function} onClose 关闭回调（由列表页负责把 URL 切回列表）
 * @param {string} testId   测试 id 前缀，默认 crm-detail-drawer
 * @param {boolean} flushTop 内容区不加上内边距。给「自带全宽吸顶头」的详情用（如已签约客户详情）：
 *   原因 —— sticky 的约束顶边是「滚动容器的内容框顶」，若内容区有上内边距，吸顶头就只能卡在
 *   内容框顶、上方留出一条空档（实测 31.5px），滚动时下面的卡片会从空档里露出来；
 *   而若用负上边距去抵消内边距，负边距会与父级发生 margin 折叠、把父元素整体上提，
 *   吸顶头反而与后一张卡片重叠。两者都错，正解是**内容区干脆不加内边距**，
 *   吸顶头自然贴住内容框顶 = 滚动容器顶，吸顶位置才正确。
 */
const DRAWER_DEFAULT_RATIO = 0.7;   // 打开时默认宽度 = 视口宽 × 70%
const DRAWER_MAX_RATIO = 0.92;      // 拖拽上限 = 视口宽 × 92%
const DRAWER_MIN_W = 480;           // 下限：再窄内容就挤成一列了
const WIDTH_KEY = "crm.drawer.width";   // 第 0 层（列表页抽屉）的宽度记忆键
const STACKED_NARROWER_PX = 70;         // 每深一层，默认宽度再窄 70px —— 多层级抽屉的视觉层级感
// 层级 → z-index：第 0 层 45（高于顶栏 z-30 / 侧栏 z-40），每深一层 +1，最多到 49。
// ⚠️ 必须**始终低于 50**：`components/ui/dialog.jsx` 的 Radix 弹窗是 z-50，
//    抽屉一旦 ≥50，抽屉里再开弹窗就会被压在下面（详见文件顶部「关键取舍①」）。
// 类名必须写成静态字面量，Tailwind 才会把它编进 CSS。
const Z_BY_LEVEL = ["z-[45]", "z-[46]", "z-[47]", "z-[48]", "z-[49]"];

// 模块级：当前已打开的抽屉栈，用来让 Esc 只关**最上面那一层**（多层级时一次 Esc 不该连关两层）。
// 判断「谁在最上」用**层级 level** 而不是挂载顺序 —— 全局线索抽屉挂在 <Routes> 之前，
// 比路由里的抽屉更早挂载，靠挂载顺序会判反。
const openDrawers = [];
let drawerSeq = 0;
// 同页详情可以多层打开，也可能由路由一次卸载多个层级；引用计数避免先卸载底层时
// 提前释放滚动、最后一层又把旧的 hidden 恢复回去，造成背景不能再滑动。
let scrollLockCount = 0;
let restoreDocumentScroll = null;

export const CrmDetailDrawer = ({ title, onClose, children, testId = "crm-detail-drawer", flushTop = false, level = 0 }) => {
  const { lang } = useApp();
  const zh = lang === "zh";
  const panelRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const mobileRef = useRef(isMobile);
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = ++drawerSeq;
  const zCls = Z_BY_LEVEL[Math.min(level, Z_BY_LEVEL.length - 1)];
  // 第 0 层用主记忆键；第 1 层及更深各自一个键 —— 否则拖动某一层会把另一层的宽度一起改掉
  const widthKey = level === 0 ? WIDTH_KEY : `${WIDTH_KEY}.L${level}`;

  // 宽度：优先读本层记忆值（用户手动拖过的宽度），没有或非法则回落默认值
  const clampW = (w) => Math.min(Math.max(w, DRAWER_MIN_W), Math.round(window.innerWidth * DRAWER_MAX_RATIO));
  // 默认宽度：第 0 层 = 视口宽 × 70%；更深层 = 「上一层宽度 − 70px × 层数」，
  // 上一层宽度取自第 0 层的记忆值（没拖过就用 70vw），从而稳定得到「第二层比第一层窄 70px」。
  const defaultW = () => {
    const base = Math.round(window.innerWidth * DRAWER_DEFAULT_RATIO);
    if (level === 0) return base;
    const v = parseInt(localStorage.getItem(WIDTH_KEY) || "", 10);
    return (Number.isFinite(v) ? v : base) - STACKED_NARROWER_PX * level;
  };
  const [panelW, setPanelW] = useState(() => {
    const v = parseInt(localStorage.getItem(widthKey) || "", 10);
    return Number.isFinite(v) ? clampW(v) : clampW(defaultW());
  });
  // 拖动中：临时关掉 max-width 过渡动画，否则拖动会「追着鼠标走」有延迟感
  const [dragging, setDragging] = useState(false);
  // 手机只切显示模式，不把桌面宽度裁到手机宽。回到桌面读取原偏好，防止横竖屏
  // 切换或浏览器缩窄后，所有后续详情都被误记成很窄的一条。
  useEffect(() => {
    const onWinResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        if (mobileRef.current) {
          const saved = parseInt(localStorage.getItem(widthKey) || "", 10);
          setPanelW(clampW(Number.isFinite(saved) ? saved : defaultW()));
        } else {
          setPanelW((w) => clampW(w));
        }
      }
      mobileRef.current = mobile;
    };
    window.addEventListener("resize", onWinResize);
    return () => window.removeEventListener("resize", onWinResize);
  }, []); // eslint-disable-line

  // 打开期间锁住文档滚动：否则页面自身的竖向滚动条会与面板滚动条在右侧并排出现两条。
  // 锁滚动时补上滚动条等宽的 padding-right，避免背景页面横向跳动；卸载时完整还原。
  useEffect(() => {
    if (scrollLockCount === 0) {
      const de = document.documentElement, body = document.body;
      const prevHtmlOverflow = de.style.overflow, prevBodyPad = body.style.paddingRight;
      const sbw = window.innerWidth - de.clientWidth;
      de.style.overflow = "hidden";
      if (sbw > 0) body.style.paddingRight = `${sbw}px`;
      restoreDocumentScroll = () => { de.style.overflow = prevHtmlOverflow; body.style.paddingRight = prevBodyPad; };
    }
    scrollLockCount += 1;
    return () => {
      scrollLockCount -= 1;
      if (scrollLockCount === 0) { restoreDocumentScroll?.(); restoreDocumentScroll = null; }
    };
  }, []);

  // 注册到抽屉栈（供 Esc 判定谁在最上层），卸载时摘除
  useEffect(() => {
    const me = { id: idRef.current, level };
    openDrawers.push(me);
    return () => { const i = openDrawers.indexOf(me); if (i >= 0) openDrawers.splice(i, 1); };
  }, [level]);

  // Esc 关闭（与弹窗习惯一致）。
  // ⚠️ 多层级抽屉时只关**最上面那一层**：否则一次 Esc 会把线索抽屉连同它下面的已签约抽屉一起关掉，
  // 用户会直接丢掉刚才所在的位置。
  useEffect(() => {
    const onKey = (e) => {
      // 内层 Radix 弹窗已消费 Escape 时只关闭内层，不能顺带把正在编辑的项目详情卸载。
      if (e.key !== "Escape" || e.defaultPrevented) return;
      const maxLevel = openDrawers.reduce((m, d) => Math.max(m, d.level), -1);
      if (maxLevel > level) return;                                   // 上面还有更深的抽屉 → 交给它
      const sameLevel = openDrawers.filter((d) => d.level === level);
      if (sameLevel.length && sameLevel[sameLevel.length - 1].id !== idRef.current) return;
      e.preventDefault();
      onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, level]);

  // 拖拽调宽：面板右对齐 → 鼠标向左移 = 变宽。松手时才写 localStorage；
  // 只有真正拖动过（latest !== startW）才落盘，否则在拖拽条上单击也会把当前宽度误写成用户偏好。
  const onResizeStart = (e) => {
    e.preventDefault();
    const startX = e.clientX, startW = panelW;
    let latest = startW;
    setDragging(true);
    const onMove = (ev) => { const next = clampW(startW - (ev.clientX - startX)); latest = next; setPanelW(next); };
    const onUp = () => {
      setDragging(false);
      if (latest !== startW) localStorage.setItem(widthKey, String(latest));
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };
  // 双击复位 = 回到「默认宽度」：默认值随视口变化，所以删掉记忆值而不是写入固定像素，
  // 否则换到别的屏幕尺寸后复位会回到旧屏幕上的那个绝对值。
  const resetW = () => { setPanelW(clampW(defaultW())); localStorage.removeItem(widthKey); };

  return (
    <div className={`fixed inset-0 ${zCls} flex justify-end`} data-testid={testId} data-drawer-level={level}>
      {/* 遮罩：点击关闭（与设计抽屉一致，40% 深色） */}
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} data-testid={`${testId}-backdrop`} />
      <div
        ref={panelRef}
        style={{ maxWidth: isMobile ? "100%" : panelW }}
        className={`relative w-full h-[100dvh] md:h-full bg-slate-50 shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-right duration-200 ${dragging || isMobile ? "" : "transition-[max-width]"}`}
        data-testid={`${testId}-panel`}
        data-mobile={isMobile ? "1" : "0"}
        data-width={isMobile ? "full" : panelW}>
        {/* 顶部细栏：抽屉自身的标识 + 关闭。内容区各页仍保留自己的表头（返回按钮 / 刷新等），互不冲突。 */}
        <div className="shrink-0 min-h-[calc(3.5rem+env(safe-area-inset-top))] pt-[env(safe-area-inset-top)] md:min-h-11 md:pt-0 bg-white border-b border-slate-200 flex items-center gap-2 px-3">
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-700" data-testid={`${testId}-title`}>{title}</span>
          <button type="button" onClick={onClose} title={zh ? "关闭（Esc）" : "Close (Esc)"} aria-label={zh ? "关闭" : "Close"}
            data-testid={`${testId}-close`}
            className="ml-auto flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        {/* 内容区：左右 padding 取 .page-pad-x、上下与 .app-page-content 同档，详情页里「负边距抵消留白」的通栏写法可直接生效 */}
        <div className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain page-pad-x pb-[calc(1rem+env(safe-area-inset-bottom))] md:pb-5 lg:pb-6 ${flushTop ? "" : "pt-4 sm:pt-5 lg:pt-6"}`} data-testid={`${testId}-body`}>
          {children}
        </div>
        {/* 左边缘拖拽条：**挂在面板内部**，而不是抽屉根节点上。
            原因：面板入场有 slide-in-from-right 动画，若拖拽条挂在根节点、按最终宽度绝对定位，
            它会在面板还在屏外滑动时就先出现在最终位置（表现为左侧先冒出一条孤立的线）。
            放进面板内则天然贴着面板左边缘、随面板一起滑入，也不受内容滚动影响
            （面板本身是 overflow-hidden 的弹性列，滚动的是内层 body）。 */}
        {!isMobile && <div onMouseDown={onResizeStart} onDoubleClick={resetW} title={zh ? "拖动调整宽度 · 双击复位" : "Drag to resize · double-click to reset"}
          className="absolute left-0 top-0 bottom-0 z-40 w-2 cursor-col-resize group" data-testid={`${testId}-resizer`}>
          <div className="absolute inset-y-0 left-0 w-px bg-slate-200 group-hover:w-1 group-hover:bg-violet-400 transition-all" />
        </div>}
      </div>
    </div>
  );
};

export default CrmDetailDrawer;
